import axios from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '@/lib/axios'

export type DriverGroupAccess = {
  canManage: boolean
  /** Grupy edytującego; null = bez ograniczeń. */
  editorGroupIds: number[] | null
  /** Tylko użytkownicy z ograniczeniem; brak wpisu = wszyscy kierowcy. */
  users: Record<string, number[]>
  vehicleEditorGroupIds: number[] | null
  /** Ograniczenia do grup pojazdów; brak wpisu = wszystkie pojazdy. */
  vehicleUsers: Record<string, number[]>
}

export type GroupAccessKind = 'drivers' | 'vehicles'

export class DriverGroupAccessError extends Error {
  constructor(public code: string) {
    super(code)
    this.name = 'DriverGroupAccessError'
  }
}

const base = (companyId: string) => `/config-api/company/${companyId}/driver-group-access`
export const driverGroupAccessKey = (companyId: string) => ['company.driverGroupAccess', companyId]

export function useQueryDriverGroupAccess(companyId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: driverGroupAccessKey(companyId ?? ''),
    enabled: enabled && !!companyId,
    retry: (failureCount, error) =>
      !(axios.isAxiosError(error) && error.response?.status === 403) && failureCount < 3,
    queryFn: async () => (await axiosInstance.get<DriverGroupAccess>(base(companyId!))).data,
  })
}

/** groupIds = null przywraca dostęp do wszystkich kierowców (albo pojazdów). */
export function useMutationSetDriverGroupAccess(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId, groupIds, kind }: { userId: number; groupIds: number[] | null; kind: GroupAccessKind }) => {
      try {
        await axiosInstance.post(`${base(companyId)}/${userId}${kind === 'vehicles' ? '/vehicles' : ''}`, { groupIds })
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const body = error.response?.data as { code?: string; Code?: string } | undefined
          throw new DriverGroupAccessError(
            body?.code || body?.Code || (error.response?.status === 403 ? 'NOT_AUTHORIZED' : 'UNKNOWN_ERROR'),
          )
        }
        throw error
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: driverGroupAccessKey(companyId) }),
  })
}
